import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, dateFmt, statusMeta, hasRole, formatErr } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2, FileText, Search, Archive, ArchiveRestore } from "lucide-react";

const STATUSES = ["draft", "in_approval", "po_issued", "approved", "rejected"];

export default function PurchaseRequests() {
    const { user } = useAuth();
    const canArchive = hasRole(user, ["school_admin", "purchasing_admin"]);
    const [rows, setRows] = useState(null);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("all");
    const [archived, setArchived] = useState(false);

    const load = () => {
        const p = new URLSearchParams();
        if (q) p.set("q", q);
        if (status !== "all") p.set("status", status);
        if (archived) p.set("archived", "true");
        api.get(`/purchase-requests?${p.toString()}`).then((r) => setRows(r.data)).catch(() => setRows([]));
    };
    useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q, status, archived]);

    const toggleArchive = async (r) => {
        try {
            await api.post(`/purchase-requests/${r.id}/archive`, { archived: !archived });
            toast.success(archived ? "Restored" : "Archived");
            load();
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    };

    return (
        <div className="space-y-6 pe-fade-up">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-brand-emerald">Purchase Requests</h1>
                    <p className="text-muted-foreground text-sm mt-1">Create, track and manage purchase requests.</p>
                </div>
                <Link to="/app/requests/new">
                    <Button className="bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid="new-request-button">
                        <Plus className="h-4 w-4 mr-1" /> New Request
                    </Button>
                </Link>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search PR # or vendor" className="pl-9" data-testid="pr-search" />
                </div>
                <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="w-[180px]" data-testid="pr-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{statusMeta(s).label}</SelectItem>)}
                    </SelectContent>
                </Select>
                {canArchive && (
                    <Button variant={archived ? "default" : "outline"} className="rounded-lg" onClick={() => setArchived((v) => !v)} data-testid="pr-archived-toggle">
                        <Archive className="h-4 w-4 mr-1" /> {archived ? "Viewing archived" : "Show archived"}
                    </Button>
                )}
            </div>

            <div className="bg-white border border-border rounded-xl overflow-hidden">
                {rows === null ? (
                    <div className="grid place-items-center py-16 text-brand-emerald"><Loader2 className="h-6 w-6 animate-spin" /></div>
                ) : rows.length === 0 ? (
                    <div className="py-16 text-center text-muted-foreground"><FileText className="h-8 w-8 mx-auto mb-3 opacity-40" /> No purchase requests found.</div>
                ) : (
                    <div className="overflow-x-auto pe-scroll">
                        <table className="w-full text-sm" data-testid="pr-table">
                            <thead>
                                <tr className="text-left text-muted-foreground border-b border-border bg-secondary/40">
                                    <th className="p-3 font-medium">PR #</th>
                                    <th className="p-3 font-medium">Vendor</th>
                                    <th className="p-3 font-medium">Requester</th>
                                    <th className="p-3 font-medium">Date</th>
                                    <th className="p-3 font-medium text-right">Amount</th>
                                    <th className="p-3 font-medium">Status</th>
                                    {canArchive && <th className="p-3 font-medium text-right">Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id} className="border-b border-border last:border-0 hover:bg-secondary/40">
                                        <td className="p-3"><Link to={`/app/requests/${r.id}`} className="font-mono text-brand-teal hover:underline" data-testid={`pr-row-${r.id}`}>{r.pr_number}</Link></td>
                                        <td className="p-3">{r.vendor_name || "—"}</td>
                                        <td className="p-3 text-muted-foreground">{r.requester_name}</td>
                                        <td className="p-3 text-muted-foreground">{dateFmt(r.created_at)}</td>
                                        <td className="p-3 text-right font-mono">{money(r.total)}</td>
                                        <td className="p-3"><StatusBadge status={r.status} /></td>
                                        {canArchive && (
                                            <td className="p-3 text-right">
                                                <Button variant="ghost" size="sm" onClick={() => toggleArchive(r)} data-testid={`archive-pr-${r.id}`}>
                                                    {archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                                                </Button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
